import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { toast } from 'sonner'
import { isRequestCanceled } from '@/utils/isRequestCanceled'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { CloserLead, CloserLeadSummary } from '@/types'
import { isRocketInstance } from '@/config/instanceProfile'
import { requestLeadConvertOptions, type ConvertLeadOptions } from '@/stores/convertLeadDialogStore'

export interface CloserLeadsFilters {
  search: string
  setSearch: (v: string) => void
  filterStatus: string
  setFilterStatus: (v: string) => void
  filterTemp: string
  setFilterTemp: (v: string) => void
  activeView: string
  setActiveView: (v: string) => void
  activeTab: string
  setActiveTab: (v: string) => void
  closerFilter: string
  setCloserFilter: (v: string) => void
  sortField: string
  setSortField: (v: string) => void
}

export function useCloserLeads() {
  const { user } = useAuthStore()
  const normalizedRole = (user?.role || '').toLowerCase()
  const isAdmin = normalizedRole === 'admin'
  const isAdminOrManager = isAdmin || normalizedRole === 'manager'

  const [leads, setLeads] = useState<CloserLead[]>([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterTemp, setFilterTemp] = useState('')
  const [activeView, setActiveView] = useState('all')
  const [activeTab, setActiveTab] = useState('all') // 'all', 'mine'
  const [closerFilter, setCloserFilter] = useState('all')
  const [sortField, setSortField] = useState('createdAt')
  const [summary, setSummary] = useState<CloserLeadSummary>({
    total: 0, novos: 0, emContato: 0, qualificados: 0,
    convertidos: 0, descartados: 0, agendamentosHoje: 0,
    byOrigin: [], byCloser: [],
  })
  const [period, setPeriod] = useState<string>('month')
  const [closersList, setClosersList] = useState<string[]>([])

  const fetchLeads = useCallback(async (signal?: AbortSignal) => {
    const isInitialLoad = !hasLoadedOnceRef.current
    if (isInitialLoad) setLoading(true)
    else setIsRefreshing(true)
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (filterStatus) params.append('status', filterStatus)
      if (filterTemp) params.append('temperature', filterTemp)
      if (sortField) params.append('sort', sortField)
      if (activeTab === 'mine') params.append('closerOnly', 'true')
      else if (isAdmin) {
        params.append('global', 'true')
        if (closerFilter !== 'all') params.append('closer', closerFilter)
      }

      const summaryParams = new URLSearchParams()
      if (isAdmin) summaryParams.append('global', 'true')
      if (closerFilter !== 'all') summaryParams.append('closer', closerFilter)
      if (activeTab === 'mine') summaryParams.append('closerOnly', 'true')
      if (period) summaryParams.append('period', period)

      const [leadsRes, summaryRes] = await Promise.allSettled([
        api.get(`/closer-leads?${params.toString()}`, { signal }),
        api.get(`/closer-leads/summary${summaryParams.toString() ? '?' + summaryParams.toString() : ''}`, { signal }),
      ])

      if (leadsRes.status === 'fulfilled') {
        const loadedLeads: CloserLead[] = leadsRes.value.data?.data?.leads || leadsRes.value.data?.data || []
        setLeads(Array.isArray(loadedLeads) ? loadedLeads : [])
      } else if (!isRequestCanceled(leadsRes.reason)) {
        toast.error(getFriendlyError(leadsRes.reason, 'Erro ao carregar carteira'))
        console.error('Erro ao buscar closer leads:', leadsRes.reason)
      }

      if (summaryRes.status === 'fulfilled' && summaryRes.value.data?.data) {
        setSummary(summaryRes.value.data.data)
      } else if (summaryRes.status === 'rejected' && !isRequestCanceled(summaryRes.reason)) {
        console.error('Erro ao buscar resumo da carteira:', summaryRes.reason)
      }
    } catch (error: unknown) {
      if (isRequestCanceled(error)) return
      toast.error(getFriendlyError(error, 'Erro ao carregar carteira'))
      console.error('Erro ao buscar closer leads:', error)
    } finally {
      hasLoadedOnceRef.current = true
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [search, filterStatus, filterTemp, sortField, activeTab, closerFilter, isAdmin, isAdminOrManager, period])

  const fetchClosers = useCallback(async () => {
    try {
      const response = await api.get('/bdr/closers')
      const list = response.data?.data || response.data || []
      setClosersList(list.map((c: any) => c.name).sort())
    } catch (error) {
      console.error('Erro ao buscar closers:', error)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchLeads(controller.signal)
    return () => controller.abort()
  }, [fetchLeads])

  useEffect(() => {
    fetchClosers()
  }, [fetchClosers])

  const closers = useMemo(() => {
    const list = [...closersList]
    if (isAdminOrManager && user?.name && !list.includes(user.name)) {
      list.push(user.name)
    }
    return Array.from(new Set(list)).sort()
  }, [closersList, isAdminOrManager, user?.name])

  const viewFilteredLeads = useMemo(() => {
    const getStatus = (l: CloserLead) => (l.status || '').toUpperCase()
    const discardedStatuses = ['CONVERTIDO', 'DESCARTADO', 'PERDIDO', 'NUTRICAO']

    if (activeView === 'converted') {
      return leads.filter((l) => getStatus(l) === 'CONVERTIDO' && !l.exclusividade)
    }
    if (activeView === 'nurturing') {
      return leads.filter((l) => getStatus(l) === 'NUTRICAO' && !l.exclusividade)
    }
    if (activeView === 'outro') {
      return leads.filter((l) => Boolean(l.exclusividade) && !discardedStatuses.includes(getStatus(l)))
    }
    if (activeView === 'all') {
      return leads.filter((l) => !discardedStatuses.includes(getStatus(l)) && !l.exclusividade)
    }
    return leads.filter((l) => !l.exclusividade)
  }, [leads, activeView])

  const visibleLeads = useMemo(() => {
    if (closerFilter === 'all') return viewFilteredLeads
    return viewFilteredLeads.filter((lead) =>
      (lead.closerName || '').toLowerCase() === closerFilter.toLowerCase(),
    )
  }, [viewFilteredLeads, closerFilter])

  const handleDeleteLead = useCallback(async (leadId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead definitivamente?')) return

    try {
      await api.delete(`/closer-leads/${leadId}`)
      setLeads(prev => prev.filter(l => l.id !== leadId))
      fetchLeads()
      toast.success('Lead excluído permanentemente!')
    } catch (error: unknown) {
      toast.error(getFriendlyError(error, 'Erro ao excluir lead'))
    }
  }, [fetchLeads])

  const handleDiscardLead = useCallback(async (leadId: string, status: string, motivo: string) => {
    try {
      await api.post(`/closer-leads/${leadId}/discard`, { status, motivo })
      fetchLeads()
      toast.success('Lead descartado com sucesso!')
    } catch (error: unknown) {
      toast.error(getFriendlyError(error, 'Erro ao descartar lead'))
    }
  }, [fetchLeads])

  const handleConvertToDeal = useCallback(async (leadId: string) => {
    const existingLead = leads.find(l => l.id === leadId)
    if (existingLead?.dealId) {
      toast.info('Este lead já foi convertido em uma Negociação')
      return
    }
    let convertOptions: ConvertLeadOptions | null = null
    if (isRocketInstance) {
      convertOptions = await requestLeadConvertOptions({
        leadId,
        empresa: existingLead?.empresa,
        contato: existingLead?.contato,
        parceiroId: existingLead?.parceiroId,
      })
      if (!convertOptions) return
    } else if (!window.confirm('Deseja converter este lead em uma Negociação direta?')) {
      return
    }
    try {
      await api.post(`/closer-leads/${leadId}/converter`, { etapa: 'qualificado', valor: 0, ...convertOptions })
      toast.success('Lead convertido com sucesso!')
      fetchLeads()
    } catch (error: unknown) {
      toast.error(getFriendlyError(error, 'Erro ao converter lead'))
    }
  }, [fetchLeads, leads])

  return {
    leads,
    visibleLeads,
    loading,
    isRefreshing,
    summary,
    closers,
    user,
    isAdmin,
    isAdminOrManager,
    fetchLeads,
    handleDeleteLead,
    handleDiscardLead,
    handleConvertToDeal,
    period,
    setPeriod,
    filters: {
      search, setSearch,
      filterStatus, setFilterStatus,
      filterTemp, setFilterTemp,
      activeView, setActiveView,
      activeTab, setActiveTab,
      closerFilter, setCloserFilter,
      sortField, setSortField,
    } as CloserLeadsFilters,
  }
}
